// Batch E2.5 — usePipelineCounts() hook.
//
// Module-level cache with a 30 s stale-time + a manual invalidation hook
// shared by every consumer (Portfolio tabs + Dashboard widget + StageBadge
// after a successful PATCH). No external dep — react-query / SWR aren't
// in the bundle and pulling them in just for this would be overkill.
//
// API:
//   const { counts, total, loading, error, refetch } = usePipelineCounts();
//   invalidatePipelineCounts();   // call after a mutation
//
// The hook re-renders all subscribers whenever invalidate is called, so
// changing a deal's stage in Portfolio immediately bumps the Dashboard
// widget on the next mount (and live, if Dashboard happens to be the page
// running concurrently).
//
// Production hardening (Phase 36 — Dashboard P0 follow-up):
//   • 10 s request timeout — silent fetch failures used to leave the
//     consumer stuck on "Loading…" forever.
//   • Explicit error state surfaced so PipelineGlanceCard can render a
//     graceful empty / retry state instead of an infinite spinner.

import { useCallback, useEffect, useState } from "react";
import api from "./api";

const STALE_MS = 30_000;
const REQUEST_TIMEOUT_MS = 10_000;
// Phase 36 — Dashboard P0 follow-up #2. /api/pipeline/counts sometimes
// fires before the session cookie is established on first navigation
// (well-documented SSR / cookie-set race that already affects
// /api/auth/me). Auto-retry exactly once after a short delay so the
// widget recovers without forcing the user to tap Retry.
const RETRY_DELAY_MS = 1_500;

let CACHE = null;         // { counts, total, fetchedAt }
let INFLIGHT = null;       // shared promise so concurrent mounts dedupe
let LAST_ERROR = null;     // last network/timeout error message
let VERSION = 0;           // bump on invalidate → forces refetch
const SUBSCRIBERS = new Set();

function notify() {
  SUBSCRIBERS.forEach((cb) => {
    try { cb(); } catch { /* noop */ }
  });
}

export function invalidatePipelineCounts() {
  CACHE = null;
  INFLIGHT = null;
  LAST_ERROR = null;
  VERSION += 1;
  notify();
}

async function fetchCounts({ _isRetry = false } = {}) {
  if (INFLIGHT) return INFLIGHT;
  INFLIGHT = api
    .get(`/pipeline/counts`, {
      timeout: REQUEST_TIMEOUT_MS,
    })
    .then((r) => {
      CACHE = { ...r.data, fetchedAt: Date.now() };
      LAST_ERROR = null;
      INFLIGHT = null;
      notify();
      return CACHE;
    })
    .catch(async (e) => {
      INFLIGHT = null;
      // Phase 36 P0 follow-up #2 — auto-retry once on 401 (auth race
      // during first page load). The retry only fires for non-retry
      // attempts; if the second attempt also fails we surface the
      // terminal error to the consumer.
      const status = e?.response?.status;
      if (!_isRetry && status === 401) {
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
        return fetchCounts({ _isRetry: true });
      }
      // Capture a short, user-safe error message. Axios timeouts surface
      // as code "ECONNABORTED"; network failures land as ERR_NETWORK.
      LAST_ERROR =
        e?.code === "ECONNABORTED"
          ? "timeout"
          : status === 401
            ? "unauthorized"
            : "network";
      notify();
      throw e;
    });
  return INFLIGHT;
}

export default function usePipelineCounts() {
  const [, tick] = useState(0);

  // Subscribe so module-level invalidate / fetch completions re-render.
  useEffect(() => {
    const cb = () => tick((n) => n + 1);
    SUBSCRIBERS.add(cb);
    return () => { SUBSCRIBERS.delete(cb); };
  }, []);

  // Trigger fetch on first mount or when stale.
  useEffect(() => {
    const isFresh = CACHE && Date.now() - CACHE.fetchedAt < STALE_MS;
    if (!isFresh) {
      fetchCounts().catch(() => { /* error surfaced via LAST_ERROR + notify */ });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => {
    invalidatePipelineCounts();
    return fetchCounts().catch(() => null);
  }, []);

  return {
    counts: CACHE?.counts || null,
    total:  CACHE?.total  ?? null,
    // Loading is true only while we have NEITHER cache NOR a final error.
    // Once an error lands, loading drops to false so consumers can render
    // their error / empty fallback (preventing infinite "Loading…").
    loading: !CACHE && !LAST_ERROR,
    error: LAST_ERROR,
    refetch,
  };
}
